import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Play, Send, Share2, Volume2, VolumeX, X } from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@shared/util/env";

const GUEST_NAME = "Guest";

function VertCard({ clip, active, shouldLoad, onObserve, onUnobserve, onComments }) {
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!shouldLoad) {
      video.pause();
      video.removeAttribute("src");
      video.load();
    } else if (active) {
      video.play().catch(() => {});
      setPaused(false);
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [active, shouldLoad]);

  useEffect(() => {
    const card = cardRef.current;
    const video = videoRef.current;
    if (card) onObserve(card);
    return () => {
      if (card) onUnobserve(card);
      if (video) {
        video.pause();
        video.removeAttribute("src");
        video.load();
      }
    };
  }, [onObserve, onUnobserve]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      setPaused(false);
    } else {
      video.pause();
      setPaused(true);
    }
  };

  const share = async () => {
    const shareData = {
      title: clip.headline || clip.sourceTitle || "VideoBrief Verts",
      text: clip.quote,
      url: window.location.href,
    };
    if (navigator.share) {
      await navigator.share(shareData).catch(() => {});
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <article className="verts-card" data-clip-id={clip.id} ref={cardRef}>
      <video
        ref={videoRef}
        src={shouldLoad ? clip.mediaUrl : undefined}
        muted={muted}
        loop
        playsInline
        preload={shouldLoad ? "auto" : "none"}
        onClick={togglePlayback}
      />
      <div className="verts-shade" />
      {paused && <div className="verts-play"><Play fill="currentColor" /></div>}
      <div className="verts-actions">
        <button type="button" onClick={() => setMuted((value) => !value)} aria-label={muted ? "Unmute" : "Mute"}>
          {muted ? <VolumeX /> : <Volume2 />}
        </button>
        <button type="button" onClick={() => onComments(clip)} aria-label="Comments">
          <MessageCircle />
        </button>
        <button type="button" onClick={share} aria-label="Share">
          <Share2 />
        </button>
      </div>
      <div className="verts-copy">
        {clip.speaker && <div className="verts-speaker">@{clip.speaker}</div>}
        <h2>{clip.headline || clip.sourceTitle || "VideoBrief Vert"}</h2>
        <p>&ldquo;{clip.quote}&rdquo;</p>
        {clip.sourceTitle && <div className="verts-source">{clip.sourceTitle}</div>}
      </div>
    </article>
  );
}

function CommentsSheet({ apiBase, token, clip, onClose }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    let active = true;
    axios.get(`${apiBase}/quote-portal/public/${token}/comments/${clip.id}`)
      .then(({ data }) => active && setComments(data))
      .catch(() => active && setComments([]));
    return () => { active = false; };
  }, [apiBase, clip.id, token]);

  const send = async () => {
    const next = text.trim();
    if (!next) return;
    setText("");
    const optimistic = { _id: `local-${Date.now()}`, userName: GUEST_NAME, text: next };
    setComments((current) => [...current, optimistic]);
    try {
      const { data } = await axios.post(
        `${apiBase}/quote-portal/public/${token}/comments/${clip.id}`,
        { userName: GUEST_NAME, text: next }
      );
      setComments((current) => current.map((item) => item._id === optimistic._id ? data : item));
    } catch {
      setComments((current) => current.filter((item) => item._id !== optimistic._id));
    }
  };

  return (
    <div className="verts-sheet-backdrop" onClick={onClose}>
      <section className="verts-sheet" onClick={(event) => event.stopPropagation()}>
        <header>
          <strong>Comments</strong>
          <button type="button" onClick={onClose} aria-label="Close comments"><X /></button>
        </header>
        <div className="verts-comments">
          {comments.length ? comments.map((comment) => (
            <div className="verts-comment" key={comment._id}>
              <span>{String(comment.userName || "G").slice(0, 1).toUpperCase()}</span>
              <div><strong>{comment.userName || GUEST_NAME}</strong><p>{comment.text}</p></div>
            </div>
          )) : <p className="verts-empty">Start the conversation.</p>}
        </div>
        <div className="verts-compose">
          <input
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && send()}
            placeholder="Add a comment"
            maxLength={500}
          />
          <button type="button" onClick={send} aria-label="Send comment"><Send /></button>
        </div>
      </section>
    </div>
  );
}

export default function VertsShare() {
  const { token } = useParams();
  const apiBase = API_URL || `${window.location.origin}/api`;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("All");
  const [activeId, setActiveId] = useState("");
  const [commentsClip, setCommentsClip] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    axios.get(`${apiBase}/quote-portal/public/${token}`)
      .then(({ data: payload }) => {
        setData(payload);
        setActiveId(payload.clips?.[0]?.id || "");
        document.title = `${payload.title || "Verts"} · VideoBrief`;
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.msg || "This Verts page is unavailable.");
      });
  }, [apiBase, token]);

  const clips = useMemo(() => {
    if (!data?.clips) return [];
    if (category === "All") return data.clips;
    return data.clips.filter((clip) => clip.categories?.includes(category));
  }, [category, data]);
  const activeIndex = clips.findIndex((clip) => clip.id === activeId);

  useEffect(() => {
    if (clips.length && !clips.some((clip) => clip.id === activeId)) {
      setActiveId(clips[0].id);
    }
  }, [activeId, clips]);

  const observe = useCallback((node) => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const id = visible?.target?.dataset?.clipId;
        if (id) setActiveId(id);
      }, { threshold: [0.55, 0.8] });
    }
    observerRef.current.observe(node);
  }, []);
  const unobserve = useCallback((node) => {
    observerRef.current?.unobserve(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  if (error) {
    return <><style>{CSS_TEXT}</style><main className="verts-state"><h1>Verts unavailable</h1><p>{error}</p></main></>;
  }
  if (!data) return <><style>{CSS_TEXT}</style><main className="verts-state"><p>Loading Verts…</p></main></>;
  if (!data.clips?.length) {
    return <><style>{CSS_TEXT}</style><main className="verts-state"><h1>{data.title}</h1><p>Vertical clips are still being prepared.</p></main></>;
  }

  return (
    <main className="verts-page">
      <style>{CSS_TEXT}</style>
      <header className="verts-top">
        <div><b>VideoBrief</b><span>{data.title}</span></div>
        <nav aria-label="Clip categories">
          {["All", ...(data.categories || [])].map((label) => (
            <button
              type="button"
              key={label}
              className={category === label ? "active" : ""}
              onClick={() => setCategory(label)}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>
      <section className="verts-feed">
        {clips.map((clip, index) => (
          <VertCard
            key={clip.id}
            clip={clip}
            active={activeId === clip.id}
            shouldLoad={index === activeIndex || index === activeIndex + 1}
            onObserve={observe}
            onUnobserve={unobserve}
            onComments={setCommentsClip}
          />
        ))}
      </section>
      {commentsClip && (
        <CommentsSheet
          apiBase={apiBase}
          token={token}
          clip={commentsClip}
          onClose={() => setCommentsClip(null)}
        />
      )}
    </main>
  );
}

const CSS_TEXT = `
  html:has(.verts-page), body:has(.verts-page), #root:has(.verts-page) { margin: 0; height: 100%; overflow: hidden; background: #05060a; }
  .verts-page, .verts-page * { box-sizing: border-box; }
  .verts-page { min-height: 100svh; background: #05060a; color: #fff; font-family: ui-sans-serif, system-ui, sans-serif; }
  .verts-top { position: fixed; z-index: 30; top: 0; left: 50%; transform: translateX(-50%); width: min(100%, 520px); padding: 12px 12px 8px; background: linear-gradient(#05060ae8, transparent); }
  .verts-top > div { display: flex; align-items: baseline; gap: 8px; padding: 0 4px 8px; }
  .verts-top b { font-size: 17px; } .verts-top span { color: #cbd5e1; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .verts-top nav { display: flex; gap: 7px; overflow-x: auto; scrollbar-width: none; }
  .verts-top nav::-webkit-scrollbar { display: none; }
  .verts-top nav button { flex: 0 0 auto; border: 1px solid #ffffff28; border-radius: 999px; padding: 7px 12px; color: #e2e8f0; background: #111827bb; font-weight: 700; font-size: 12px; }
  .verts-top nav button.active { background: #7c3aed; border-color: #a78bfa; color: #fff; }
  .verts-feed { height: 100svh; overflow-y: auto; scroll-snap-type: y mandatory; overscroll-behavior-y: contain; }
  .verts-card { position: relative; width: min(100%, 520px); height: 100svh; margin: 0 auto; background: #000; scroll-snap-align: start; scroll-snap-stop: always; overflow: hidden; }
  .verts-card video { width: 100%; height: 100%; object-fit: cover; background: #000; }
  .verts-shade { position: absolute; inset: 0; pointer-events: none; background: linear-gradient(transparent 48%, rgba(0,0,0,.15) 60%, rgba(0,0,0,.88) 100%); }
  .verts-play { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
  .verts-play svg { width: 68px; height: 68px; padding: 18px; border-radius: 50%; background: #0008; }
  .verts-actions { position: absolute; z-index: 4; right: 12px; bottom: 138px; display: flex; flex-direction: column; gap: 14px; }
  .verts-actions button { width: 48px; height: 48px; border: 1px solid #ffffff2e; border-radius: 50%; color: #fff; background: #0f172abf; display: grid; place-items: center; backdrop-filter: blur(8px); }
  .verts-actions svg { width: 22px; height: 22px; }
  .verts-copy { position: absolute; z-index: 3; left: 16px; right: 72px; bottom: 24px; text-shadow: 0 2px 12px #000; }
  .verts-speaker { font-size: 13px; font-weight: 800; color: #c4b5fd; margin-bottom: 5px; }
  .verts-copy h2 { font-size: 18px; margin: 0 0 6px; line-height: 1.2; }
  .verts-copy p { font-size: 14px; line-height: 1.42; margin: 0; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
  .verts-source { margin-top: 8px; color: #cbd5e1; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .verts-state { min-height: 100svh; display: grid; align-content: center; justify-items: center; padding: 30px; text-align: center; background: #05060a; color: #fff; font-family: ui-sans-serif, system-ui, sans-serif; }
  .verts-state p { color: #94a3b8; }
  .verts-sheet-backdrop { position: fixed; z-index: 60; inset: 0; background: #0009; display: flex; align-items: flex-end; justify-content: center; }
  .verts-sheet { width: min(100%, 520px); height: min(68svh, 620px); border-radius: 24px 24px 0 0; background: #111827; border: 1px solid #ffffff1f; display: flex; flex-direction: column; overflow: hidden; }
  .verts-sheet header { height: 56px; display: flex; align-items: center; justify-content: space-between; padding: 0 16px; border-bottom: 1px solid #ffffff14; }
  .verts-sheet header button { border: 0; background: none; color: #fff; }
  .verts-comments { flex: 1; overflow-y: auto; padding: 16px; }
  .verts-comment { display: flex; gap: 10px; margin-bottom: 16px; }
  .verts-comment > span { flex: 0 0 34px; width: 34px; height: 34px; border-radius: 50%; background: #7c3aed; display: grid; place-items: center; font-weight: 800; }
  .verts-comment strong { font-size: 12px; color: #c4b5fd; } .verts-comment p { margin: 3px 0 0; line-height: 1.4; font-size: 14px; }
  .verts-empty { text-align: center; color: #94a3b8; margin-top: 60px; }
  .verts-compose { display: flex; gap: 8px; padding: 12px; border-top: 1px solid #ffffff14; }
  .verts-compose input { flex: 1; border: 1px solid #ffffff1f; border-radius: 999px; background: #020617; color: #fff; padding: 12px 16px; outline: none; }
  .verts-compose button { width: 44px; border: 0; border-radius: 50%; background: #7c3aed; color: #fff; display: grid; place-items: center; }
  @media (min-width: 700px) { .verts-card { border-left: 1px solid #ffffff12; border-right: 1px solid #ffffff12; } }
`;
